// What the player is doing right now, as a tiny React-free store (issue #3517 item B1, extended
// for the update gate in issue #3265).
//
// The cache refresher replaces a song's files in place when the server's copy changes. Reloading
// those files into memory under a *playing* song would interrupt playback, so the reload is gated
// on "not playing" - but the code that needs to know sits ABOVE the sequencer's context
// (CurrentSongProvider, and the PDF viewer's load effect, which must not re-run every time
// playback starts or stops). This store lets them read the current playback state without taking a
// React dependency on it.
//
// Issue #3265 routes app reloads (and, in a later step, the current song's files) through
// update_gate.ts, whose policy needs two more facts this store is the only place to keep: whether
// the song on screen has been played since it loaded, and how long ago playback last stopped.
// Pause and stop are the same thing here - a leader between songs may use either - so both arrive
// as setIsPlaying(false).
//
// The sequencer is the single writer; everybody else reads.

export interface PlaybackSnapshot {
  isPlaying: boolean;
  // true once playback has started for the song currently loaded; false again after a transition
  hasPlayedCurrentSong: boolean;
  // when playback last went from playing to not playing; undefined until it first does
  lastStoppedAt: number | undefined;
}

let isPlaying = false;
let hasPlayedCurrentSong = false;
let lastStoppedAt: number | undefined;
const isPlayingListeners = new Set<() => void>();
const songTransitionListeners = new Set<() => void>();

function notify(listeners: Set<() => void>): void {
  // copy the set so a listener that unsubscribes itself does not disturb the iteration
  for (const listener of [...listeners]) {
    listener();
  }
}

export function setIsPlaying(value: boolean): void {
  if (value === isPlaying) {
    return;
  }
  isPlaying = value;
  if (value) {
    hasPlayedCurrentSong = true;
  } else {
    lastStoppedAt = Date.now();
  }
  notify(isPlayingListeners);
}

// A load failed, so nothing is playing and nothing was heard: the song reads as never played, and
// no stop is stamped. Without this a 404 for files not yet generated would count as a stop, and the
// very hash change that recovers the song would be held for the idle window (issue #3265).
export function noteLoadFailed(): void {
  const changed = isPlaying || hasPlayedCurrentSong;
  isPlaying = false;
  hasPlayedCurrentSong = false;
  if (changed) {
    notify(isPlayingListeners);
  }
}

export function getIsPlaying(): boolean {
  return isPlaying;
}

export function subscribeIsPlaying(listener: () => void): () => void {
  isPlayingListeners.add(listener);
  return () => {
    isPlayingListeners.delete(listener);
  };
}

// Raised by the sequencer when the song loaded in the player changes (a new slug, including the
// first one). A song's files being refreshed in place is NOT a transition: it is the same song,
// and if it was played before the refresh it still counts as played.
export function noteSongTransition(): void {
  // playback carrying across the transition (auto-play, next/previous while playing) means the new
  // song is being played from its first moment; the sequencer's later play() changes nothing
  hasPlayedCurrentSong = isPlaying;
  notify(songTransitionListeners);
}

export function subscribeSongTransition(listener: () => void): () => void {
  songTransitionListeners.add(listener);
  return () => {
    songTransitionListeners.delete(listener);
  };
}

export function getPlaybackSnapshot(): PlaybackSnapshot {
  return { isPlaying, hasPlayedCurrentSong, lastStoppedAt };
}

// test-only: the store is module state, so tests must be able to put it back
export function resetPlaybackStateForTests(): void {
  isPlaying = false;
  hasPlayedCurrentSong = false;
  lastStoppedAt = undefined;
  isPlayingListeners.clear();
  songTransitionListeners.clear();
}
